import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { Editor } from '@tiptap/core';
import { checkContrastAgainstBackgrounds } from '@course-authoring/shared-types';

import { preventRichTextToolbarMouseDown } from './richTextToolbar';

function getActiveColor(editor: Editor): string {
  const attrs = editor.getAttributes('textStyle') as Record<string, unknown>;
  return typeof attrs['color'] === 'string' ? attrs['color'] : '';
}

interface ContrastBadgeProps {
  bgColor: string;
  passes: boolean;
  ratio: number | null;
}

function ContrastBadge({ bgColor, passes, ratio }: ContrastBadgeProps): ReactNode {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '11px',
      }}
      title={`Contrast ratio: ${ratio !== null ? ratio.toFixed(1) : '?'}:1`}
    >
      <div
        style={{
          width: '16px',
          height: '16px',
          borderRadius: '3px',
          backgroundColor: bgColor,
          border: '1px solid rgba(0,0,0,0.15)',
          flexShrink: 0,
        }}
      />
      <span style={{ color: passes ? '#388E3C' : '#D32F2F', fontWeight: 600 }}>
        {passes ? 'AA ✓' : 'AA ✗'}
      </span>
      <span style={{ color: '#666' }}>
        {ratio !== null ? `${ratio.toFixed(1)}:1` : ''}
      </span>
    </div>
  );
}

interface RichTextFontColorPickerProps {
  editor: Editor;
  backgroundColors: string[];
  buttonStyle?: CSSProperties;
  activeButtonStyle?: CSSProperties;
}

export function RichTextFontColorPicker({
  editor,
  backgroundColors,
  buttonStyle,
  activeButtonStyle,
}: RichTextFontColorPickerProps): ReactNode {
  const [isOpen, setIsOpen] = useState(false);
  const [hexInput, setHexInput] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const hexInputRef = useRef<HTMLInputElement>(null);
  const activeColor = getActiveColor(editor);

  const previewColor = hexInput.length >= 4 && /^#[0-9a-fA-F]+$/.test(hexInput)
    ? hexInput
    : activeColor || '#000000';

  // Filter out dark backgrounds for WCAG check (only check against light backgrounds)
  const lightBackgroundColors = backgroundColors.filter((color) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  });

  const contrastResults = lightBackgroundColors.length > 0
    ? checkContrastAgainstBackgrounds(previewColor, lightBackgroundColors)
    : [];

  const hasContrastFailure = contrastResults.some(
    (r: { passes: boolean }) => !r.passes
  );

  const applyColor = useCallback(
    (color: string) => {
      editor.chain().focus().setColor(color).run();
      setIsOpen(false);
      setHexInput('');
    },
    [editor]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setHexInput('');
  }, []);

  const handleHexSubmit = useCallback(() => {
    if (/^#[0-9a-fA-F]{3,6}$/.test(hexInput)) {
      applyColor(hexInput);
    }
  }, [hexInput, applyColor]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  const popoverStyle: CSSProperties = {
    position: 'fixed',
    zIndex: 10000,
    padding: '12px',
    backgroundColor: '#fff',
    border: '1px solid rgba(0,0,0,0.15)',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    minWidth: '240px',
  };

  // Position popover below the button using getBoundingClientRect
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseDown={preventRichTextToolbarMouseDown}
    >
      <button
        type="button"
        onClick={() => {
          if (isOpen) {
            handleClose();
          } else {
            setIsOpen(true);
            setHexInput('');
          }
        }}
        style={{
          ...(buttonStyle ?? {}),
          ...(activeColor ? (activeButtonStyle ?? {}) : {}),
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
        }}
        title="Font color"
      >
        <span>A</span>
        <div
          style={{
            width: '14px',
            height: '3px',
            backgroundColor: activeColor || '#000000',
            borderRadius: '1px',
          }}
        />
      </button>

      {isOpen ? (
        <div
          style={{ ...popoverStyle, top: popoverPos.top, left: popoverPos.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header with close button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>
              Font Color
            </span>
            <button
              type="button"
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#666',
                padding: '0 2px',
                lineHeight: 1,
              }}
              title="Close"
              aria-label="Close color picker"
            >
              ✕
            </button>
          </div>

          {/* Color preview + hex input */}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              marginBottom: contrastResults.length > 0 ? '8px' : 0,
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '4px',
                backgroundColor: previewColor,
                border: '1px solid rgba(0,0,0,0.15)',
                flexShrink: 0,
              }}
              title={`Preview: ${previewColor}`}
            />
            <input
              ref={hexInputRef}
              type="text"
              value={hexInput}
              onChange={(e) => setHexInput(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                hexInputRef.current?.focus();
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  handleHexSubmit();
                }
                if (e.key === 'Escape') {
                  handleClose();
                }
              }}
              placeholder={activeColor || '#000000'}
              style={{
                flex: 1,
                height: '32px',
                padding: '0 8px',
                fontSize: '13px',
                border: '1px solid rgba(0,0,0,0.15)',
                borderRadius: '4px',
                outline: 'none',
                fontFamily: 'monospace',
              }}
              aria-label="Hex color value"
            />
            <button
              type="button"
              onClick={handleHexSubmit}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                height: '32px',
                padding: '0 10px',
                fontSize: '12px',
                fontWeight: 600,
                border: '1px solid rgba(0,0,0,0.15)',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                cursor: 'pointer',
              }}
            >
              Apply
            </button>
          </div>

          {contrastResults.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: hasContrastFailure ? '#D32F2F' : '#388E3C',
                  marginBottom: '2px',
                }}
              >
                {hasContrastFailure
                  ? '⚠ Color may not meet WCAG AA on some backgrounds'
                  : '✓ Meets WCAG AA on all backgrounds'}
              </div>
              {contrastResults.map((result) => (
                <ContrastBadge
                  key={result.bgColor}
                  bgColor={result.bgColor}
                  passes={result.passes}
                  ratio={result.ratio}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
